<template>
    <div class="map__wrapper">
        <!-- Map -->
        <div
            class="map__canvas vw-100"
            :class="{ padded: isMobile && $route.query.guid }"
            ref="map"
        ></div>

        <route-planning
            :icons="icons"
            :default-route="route"
            :mobile-search="mobileSearch"
            :show-departure-datepicker="showDepartureDatepicker"
            :error="error"
            :options="options"
            :center="center"
            :overview="overview"
            :loading="calculating"
            @showError="showError"
            @findSearch="findSearch"
            @calculateRoute="calculateRoute"
            @opensidebar="opensidebar"
            @updateRoute="updateRoute"
            @setMobileSearch="setMobileSearch"
            @currentLocationToRoutePlan="currentLocationToRoutePlan"
            @updateWaypoint="updateWaypoint"
            @removeMarker="removeWaypointMarker"
            @updateTruckPresets="updateTruckPresets"
            @addNewDestination="addNewDestination"
        ></route-planning>

        <!-- Filters -->
        <quick-filters
            v-if="showPois && !isMobile"
            :selectedPrimaryFilter="selectedPrimaryFilter"
            @selectprimaryfilter="passPrimaryFilter"
            @selectsecondaryfilter="passSecondaryFilter"
        ></quick-filters>
    </div>
</template>

<script>
import { format } from 'date-fns';
import { options as inputOptions, closestWholeHour } from '../helpers/options';
import formats from '../helpers/formats';
import rasterIcons from '../helpers/rasterIcons.json';
import amenitiesIcons from '../helpers/icons.json';

export default {
    props: {
        settings: {
            type: Object,
            default() {
                return {
                    routing: {
                        fastest: true,
                        balanced: false,
                    },
                    avoid: {
                        ferries: true,
                        tolls: true,
                    },
                };
            },
        },
        selectedPrimaryFilter: {
            default: null,
        },
        showPois: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        comparableDate() {
            return this.format(this.route.date, formats.date);
        },
        comparableTime() {
            return this.format(this.route.time, formats.time);
        },
        showDepartureDatepicker() {
            return this.route.departure !== 'now';
        },
        filteredPois() {
            let { pois } = this;

            if (this.selectedPrimaryFilter) {
                const primary = this.selectedPrimaryFilter.category;
                pois = pois.filter(
                    (item) =>
                        // eslint-disable-next-line operator-linebreak
                        item.categories &&
                        item.categories.indexOf(primary) > -1,
                );
            }

            if (
                // eslint-disable-next-line operator-linebreak
                this.selectedPrimaryFilter &&
                this.selectedPrimaryFilter.children
            ) {
                const { children } = this.selectedPrimaryFilter;
                const active = children
                    .filter((child) => child.active)
                    .map(({ category }) => category);
                pois = pois.filter((item) => active.indexOf(item.icon) > -1);
            }

            return pois;
        },
        truckPreset() {
            return this.options.truck_preset[0].find(
                (item) => item.value === this.route.truck_preset,
            );
        },
    },
    data() {
        return {
            /*
            |--------------------------------------------------------------------------
            | General
            |--------------------------------------------------------------------------
            |
            | Common map settings and objects
            |
            */
            map: null, // Map instance
            devicePixelRatio: window.devicePixelRatio, // Pixel ratio of client device
            uiType: 'custom', // Type of UI to use (default|custom)

            /*
            |--------------------------------------------------------------------------
            | Map view
            |--------------------------------------------------------------------------
            |
            | Configure map view options
            |
            */
            zoom: 5, // Default zoom
            center: {
                // Default center
                lat: 39.357835,
                lng: -97.659766,
            },

            /*
            |--------------------------------------------------------------------------
            | Behavior
            |--------------------------------------------------------------------------
            |
            | Behavior settings
            |
            */
            animateMapZoom: true, // Animate map zoom or set zoom wihout animation

            /*
            |--------------------------------------------------------------------------
            | UI
            |--------------------------------------------------------------------------
            |
            | Icons and other UI elements
            |
            */
            icons: window.here.icons, // Set of icons for controls, markers etc
            defaultControlsAlignment: 'right-bottom', // Default map controls position (top-left|left-top|left-middle|left-bottom|bottom-left|top-center|bottom-center|top-right|right-top|right-middle|right-bottom|bottom-right)
            defaultUnitSystem: 'imperial', // Unit system (imperial|metric)

            /*
            |--------------------------------------------------------------------------
            | HERE
            |--------------------------------------------------------------------------
            |
            | HERE maps specific properties
            |
            */
            apiKeyHere: window.here.api_key, // Maps API key
            platform: null, // H.platform Platform object (created by HERE)
            ui: null, // H.ui UI object (created by HERE)
            layers: null, // H.map.layer.Layer (created by HERE)
            behavior: null, // H.mapevents.Behavior (created by HERE)

            /*
            |--------------------------------------------------------------------------
            | Route
            |--------------------------------------------------------------------------
            |
            | Route planning options
            |
            */
            route: {
                origin: {
                    name: null,
                    coordinates: null,
                    full: null,
                    id: null,
                },
                destination: {
                    name: null,
                    coordinates: null,
                    full: null,
                    id: null,
                },
                date: new Date(),
                time: closestWholeHour,
                departure: 'now',
                truck_preset: window.config.selected_preset,
                timeout: {
                    origin: null,
                    destination: null,
                    showOrigin: null,
                    showDestination: null,
                },
                suggestions: {
                    origin: [],
                    destination: [],
                    showOrigin: false,
                    showDestination: false,
                },
                block: {
                    origin: false,
                    destination: false,
                },
                find: {
                    origin: null,
                    destination: null,
                },
                waypoints: [],
            },

            options: {
                departure: [
                    [
                        {
                            label: 'Arrive by',
                            value: 'arrive',
                        },
                        {
                            label: 'Leave now',
                            value: 'now',
                        },
                    ],
                ],
                truck_preset: [
                    Object.values(window.config.truck_presets).map((item) => {
                        // eslint-disable-next-line no-param-reassign
                        item.value = item.type;
                        return item;
                    }),
                ],
                date: inputOptions.date,
                time: inputOptions.time,
            },

            markers: [],

            groups: {
                routes: null,
                markers: null,
            },

            error: {
                show: false,
                msg: '',
            },

            /*
            |--------------------------------------------------------------------------
            | POIs
            |--------------------------------------------------------------------------
            |
            | List of POIs to show
            |
            */
            pois: [],
            rasterIcons,
            amenitiesIcons,
            markerIcons: [],
            clusterLayer: null,
            filteringTimeout: null,

            /**
             * Mobile
             */
            mobileSearch: null,

            /*
            |--------------------------------------------------------------------------
            | Loading
            |--------------------------------------------------------------------------
            |
            | Loading indicators
            |
            */
            loading: {
                overview: false,
            },
            calculating: false,
            axiosCancelToken: null,

            /*
            |--------------------------------------------------------------------------
            | Overview
            |--------------------------------------------------------------------------
            |
            | Route overview
            |
            */
            overview: {
                pois: [],
            },
        };
    },
    mounted() {
        this.init();

        if (this.$route.query.guid) {
            this.loadRoute(this.$route.query.guid);
        }

        if (this.$route.params.poiguid && this.showPois) {
            this.onPoiDetailRouted(this.$route.params.poiguid);
        }

        if (this.$route.params.term) {
            this.route.origin.name = this.$route.params.term;
            this.findSearch(this.route.origin, 'origin');
        }

        if (typeof window.config.deep_link !== 'undefined') {
            this.solveDeepLink(window.config.deep_link);
        }

        this.handleDirectionsButton();
    },
    watch: {
        filteredPois() {
            if (this.filteringTimeout) {
                clearTimeout(this.filteringTimeout);
            }
            this.filteringTimeout = setTimeout(() => {
                this.drawClusters();
            }, 600);
        },
        settings: {
            handler() {
                axios.post('/api/v1/route-setting/save', {
                    session: window.config.session,
                    settings: this.settings,
                });
                this.calculateRoute();
            },
            deep: true,
        },
        // eslint-disable-next-line
        'route.truck_preset'() {
            this.calculateRoute();
        },
        // eslint-disable-next-line
        'overview.pois'() {
            this.drawOverviewMarkers();
        },
    },
    methods: {
        format,
        init() {
            this.initHere();
        },
        // HERE
        initHere() {
            this.initPlatformHere();
            this.initDefaultLayersHere();
            this.initMapHere();
            // his.initStyleHere();
            this.initUiHere();
            this.initBehaviorHere();

            this.registerResizeListenerHere();
            this.registerMapviewListenerHere();

            this.createContextMenu();

            if (this.showPois) {
                this.loadPois();
            }
        },
        initPlatformHere() {
            this.platform = new H.service.Platform({
                apikey: this.apiKeyHere,
            });
        },
        initDefaultLayersHere() {
            /**
             *
             *  raster: {
                    normal: {
                        base: <Layer>,
                        basenight: <Layer>,
                        labels: <Layer>,
                        map: <Layer>,
                        mapnight: <Layer>,
                        trafficincidents: <Layer>,
                        transit: <Layer>,
                        xbase: <Layer>,
                        xbasenight: <Layer>
                    },
                    satellite: {
                        base: <Layer>,
                        labels: <Layer>,
                        map: <Layer>,
                        xbase: <Layer>
                    },
                    terrain: {
                        base: <Layer>,
                        labels: <Layer>,
                        map: <Layer>,
                        xbase: <Layer>
                    }
                },
                vector: {
                    normal: {
                        map: <Layer>,
                        traffic: <Layer>,
                        trafficincidents: <Layer>
                    }
                }
             */
            this.layers = this.platform.createDefaultLayers();
        },
        initMapHere() {
            this.map = new H.Map(
                this.$refs.map,
                this.layers.raster.normal.map,
                {
                    center: {
                        lat: this.center.lat,
                        lng: this.center.lng,
                    },
                    zoom: this.zoom,
                    pixelRatio: this.devicePixelRatio || 1,
                    engineType: H.map.render.RenderEngine.EngineType.P2D,
                    padding: {
                        top: this.isMobile ? 300 : 200,
                        left: this.isMobile ? 100 : 400,
                        bottom: 100,
                        right: 100,
                    },
                },
            );
        },
        initUiHere() {
            if (this.uiType === 'default') {
                this.initUiHereDefault();
            } else {
                this.initUiHereCustom();
            }
        },
        initUiHereCustom() {
            // Create default UI
            this.ui = H.ui.UI.createDefault(this.map, this.layers);

            // Remove some buttons from it
            this.ui.removeControl('mapsettings');
            this.ui.removeControl('zoom');
            this.ui.removeControl('scalebar');

            // Store references locally
            const { icons, defaultControlsAlignment } = this;

            // Add custom buttons
            function inherits(childCtor, parentCtor) {
                const localChildCtor = childCtor;
                const localParentCtor = parentCtor;
                function TempCtor() {}
                TempCtor.prototype = parentCtor.prototype;
                // eslint-disable-next-line
                localChildCtor.superClass_ = parentCtor.prototype;
                localChildCtor.prototype = new TempCtor();
                localChildCtor.prototype.constructor = localChildCtor;

                /* eslint-disable */
                localChildCtor.base = function (me, methodName, varArgs) {
                    var args = new Array(arguments.length - 2);
                    for (var i = 2; i < arguments.length; i + 1) {
                        args[i - 2] = arguments[i];
                    }
                    return localParentCtor.prototype[methodName].apply(
                        me,
                        args,
                    );
                };
                /* eslint-enable */
            }

            // Zoom controls
            function Zoom(optOptions) {
                const options = optOptions || {};

                H.ui.Control.call(this);
                this.onZoomIn = this.onZoomIn.bind(this);
                this.onZoomOut = this.onZoomOut.bind(this);

                // Button element - zoom in
                this.zoomInBtn = new H.ui.base.Button({
                    label: `<img src="${icons.add}" width="24" height="24" alt="Zoom in">`,
                    onStateChange: this.onZoomIn,
                    data: {
                        name: 'Zoom out',
                        cssClass: 'btn--zoom-in',
                    },
                });

                // Button element - zoom out
                this.zoomOutBtn = new H.ui.base.Button({
                    label: `<img src="${icons.minus}" width="24" height="24" alt="Zoom out">`,
                    onStateChange: this.onZoomOut,
                    data: {
                        name: 'Zoom in',
                        cssClass: 'btn--zoom-out',
                    },
                });

                // Add all buttons as this control's children
                const buttons = [this.zoomInBtn, this.zoomOutBtn];

                buttons.forEach((button) => {
                    const data = button.getData();
                    button.addClass('btn--map-control');
                    button.addClass(data.cssClass);
                    this.addChild(button);
                });

                this.setAlignment(
                    options.alignment || defaultControlsAlignment,
                );

                this.options = options;
            }
            inherits(Zoom, H.ui.Control);

            Zoom.prototype.onZoomIn = (event) => {
                if (event.currentTarget.getState() === 'down') {
                    this.$gtag.event('MAP_CONTROL_ZOOM_IN', {
                        event_category: 'MAP',
                    });
                    this.zoomIn();
                }
            };

            Zoom.prototype.onZoomOut = (event) => {
                if (event.currentTarget.getState() === 'down') {
                    this.$gtag.event('MAP_CONTROL_ZOOM_OUT', {
                        event_category: 'MAP',
                    });
                    this.zoomOut();
                }
            };

            if (!this.isMobile) {
                const zoom = new Zoom();
                this.ui.addControl('zoom', zoom);
            }

            // My location controls
            function MyLocation(optOptions) {
                const options = optOptions || {};

                H.ui.Control.call(this);
                this.onMyLocation = this.onMyLocation.bind(this);

                // Button element - my location
                this.myLocationBtn = new H.ui.base.Button({
                    label: `<img src="${icons['place-outline']}" width="24" height="24" alt="My location">`,
                    onStateChange: this.onMyLocation,
                    data: {
                        name: 'My location',
                        cssClass: 'btn--my-location',
                    },
                });

                // Add all buttons as this control's children
                const buttons = [this.myLocationBtn];

                buttons.forEach((button) => {
                    const data = button.getData();
                    button.addClass('btn--map-control');
                    button.addClass(data.cssClass);
                    this.addChild(button);
                });

                this.setAlignment(
                    options.alignment || defaultControlsAlignment,
                );

                this.options = options;
            }
            inherits(MyLocation, H.ui.Control);

            MyLocation.prototype.onMyLocation = (event) => {
                if (event.currentTarget.getState() === 'down') {
                    this.$gtag.event('MAP_CONTROL_CURRENT_LOCATION', {
                        event_category: 'MAP',
                    });
                    this.centerToCurrentLocation();
                }
            };

            const myLocation = new MyLocation();
            this.ui.addControl('myLocation', myLocation);

            // Fullscreen controls
            function Fullscreen(optOptions) {
                const options = optOptions || {};

                H.ui.Control.call(this);
                this.onFullscreen = this.onFullscreen.bind(this);

                // Button element - fullscreen
                this.fullscreenBtn = new H.ui.base.Button({
                    label: `<img src="${icons.fullscreen}" width="24" height="24" alt="Fullscreen">`,
                    onStateChange: this.onFullscreen,
                    data: {
                        name: 'Fullscreen',
                        cssClass: 'btn--fullscreen',
                    },
                });

                // Add all buttons as this control's children
                const buttons = [this.fullscreenBtn];

                buttons.forEach((button) => {
                    const data = button.getData();
                    button.addClass('btn--map-control');
                    button.addClass(data.cssClass);
                    this.addChild(button);
                });

                this.setAlignment(
                    options.alignment || defaultControlsAlignment,
                );

                this.options = options;
            }
            inherits(Fullscreen, H.ui.Control);

            Fullscreen.prototype.onFullscreen = (event) => {
                if (event.currentTarget.getState() === 'down') {
                    this.$gtag.event('MAP_CONTROL_FULLSCREEN', {
                        event_category: 'MAP',
                    });
                    this.switchFullscreen();
                }
            };

            if (!this.isMobile) {
                const fullscreen = new Fullscreen();
                this.ui.addControl('fullscreen', fullscreen);
            }

            // Map type controls
            function MapType(optOptions) {
                const options = optOptions || {};

                H.ui.Control.call(this);
                this.onMapType = this.onMapType.bind(this);

                // Button element - map type
                this.mapTypeBtn = new H.ui.base.Button({
                    label: `<img src="${icons.public}" width="24" height="24" alt="Switch to satellite view">`,
                    onStateChange: this.onMapType,
                    data: {
                        name: 'Map type',
                        cssClass: 'btn--map-type',
                    },
                });

                // Add all buttons as this control's children
                const buttons = [this.mapTypeBtn];

                buttons.forEach((button) => {
                    const data = button.getData();
                    button.addClass('btn--map-control');
                    button.addClass(data.cssClass);
                    this.addChild(button);
                });

                this.setAlignment(
                    options.alignment || defaultControlsAlignment,
                );

                this.options = options;
            }
            inherits(MapType, H.ui.Control);

            MapType.prototype.onMapType = (event) => {
                if (event.currentTarget.getState() === 'down') {
                    let mapTypeAfter = '';
                    const currentLayer = this.getCurrentLayer();
                    const satellite = this.layers.raster.satellite.map;
                    if (currentLayer === satellite) {
                        mapTypeAfter = 'roadmap';
                    } else {
                        mapTypeAfter = 'satellite';
                    }
                    this.$gtag.event('MAP_CONTROL_SWITCH_MAP', {
                        event_category: 'MAP',
                        value: mapTypeAfter,
                    });
                    this.switchMapType();
                }
            };

            const mapType = new MapType();

            // Change icon on changing map type
            this.map.addEventListener('baselayerchange', () => {
                if (
                    this.getCurrentLayer() === this.layers.raster.satellite.map
                ) {
                    mapType.mapTypeBtn.setLabel(
                        `<img src="${icons.map}" width="24" height="24" alt="Switch to map view">`,
                    );
                } else {
                    mapType.mapTypeBtn.setLabel(
                        `<img src="${icons.public}" width="24" height="24" alt="Switch to satellite view">`,
                    );
                }
            });

            this.ui.addControl('mapType', mapType);

            // Set unit system
            this.setDefaultUnitSystem();
        },
        initUiHereDefault() {
            this.ui = H.ui.UI.createDefault(this.map, this.defaultLayers);
        },
        /**
         * Style settings
         *
         * @see: https://enterprise.here.com/map-style-editor/#9.842/52.5309/13.38484
         * @see: https://developer.here.com/documentation/maps/3.1.3.0/dev_guide/topics_api/h-map-style.html
         */
        initStyleHere() {
            const baseLayer = this.map.getBaseLayer();
            const baseStyle = baseLayer.getProvider().getStyle();
            const baseColor = '#eeeeee';

            const changeListener = () => {
                /* eslint-disable operator-linebreak */
                if (
                    baseStyle.getState() === H.map.Style.State.READY &&
                    baseStyle.getProperty('global.scene_background_color') !==
                        baseColor
                ) {
                    baseStyle.setProperty(
                        'global.scene_background_color',
                        baseColor,
                    );
                }
                /* eslint-enable operator-linebreak */
            };

            baseStyle.addEventListener('change', changeListener);
        },
        initBehaviorHere() {
            this.behavior = new H.mapevents.Behavior(
                new H.mapevents.MapEvents(this.map),
            );

            // Disabled behavior
            this.behavior.disable(H.mapevents.Behavior.Feature.PANNING);
            this.behavior.disable(H.mapevents.Behavior.Feature.DBL_TAP_ZOOM);
            this.behavior.disable(H.mapevents.Behavior.Feature.FRACTIONAL_ZOOM);
            this.behavior.disable(H.mapevents.Behavior.Feature.HEADING);
            this.behavior.disable(H.mapevents.Behavior.Feature.TILT);

            // Enabled behavior
            this.behavior.enable(H.mapevents.Behavior.Feature.PINCH_ZOOM);
            this.behavior.enable(H.mapevents.Behavior.Feature.WHEEL_ZOOM);
            this.behavior.enable(H.mapevents.Behavior.DRAGGING);
        },
        centerToCurrentLocation() {
            if (navigator.geolocation) {
                const success = (position) => {
                    this.centerToPosition({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                };
                const error = ({ code, message }) => {
                    this.errorGettingLocation({ code, message });
                };
                navigator.geolocation.getCurrentPosition(success, error, {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0,
                });
            }
        },
        centerToPosition({ lat, lng }) {
            this.map.setCenter({ lat, lng }, true);
        },
        errorGettingLocation() {
            // eslint-disable-next-line no-alert
            // alert(`ERROR(${error.code}): ${error.message}`); // @todo: turn into error message
            this.error = {
                show: true,
                msg:
                    'Current location is not available, please check your browser settings',
            };
        },
        registerResizeListenerHere() {
            window.addEventListener('resize', () => {
                const vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
                this.map.getViewPort().resize();
            });
            const ro = new ResizeObserver(() => {
                this.map.getViewPort().resize();
            });
            ro.observe(document.querySelector('.map__canvas'));
        },
        registerMapviewListenerHere() {
            this.map.addEventListener('mapviewchangeend', () => {
                this.center = {
                    lat: this.map.getCenter().lat,
                    lng: this.map.getCenter().lng,
                };
            });
        },
        getCurrentLayer() {
            return this.map.getBaseLayer();
        },
        switchMapType() {
            if (this.getCurrentLayer() === this.layers.raster.satellite.map) {
                this.map.setBaseLayer(this.layers.raster.normal.map);
            } else {
                this.map.setBaseLayer(this.layers.raster.satellite.map);
            }
        },
        switchFullscreen() {
            if (!document.fullscreenElement) {
                document.documentElement.requestFullscreen();
            } else if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        },
        setDefaultUnitSystem() {
            if (this.defaultUnitSystem === 'imperial') {
                this.ui.setUnitSystem(H.ui.UnitSystem.IMPERIAL);
            } else if (this.defaultUnitSystem === 'metric') {
                this.ui.setUnitSystem(H.ui.UnitSystem.METRIC);
            }
        },
        getZoom() {
            return this.map.getZoom();
        },
        zoomIn() {
            this.map.setZoom(this.getZoom() + 1, this.animateMapZoom);
        },
        zoomOut() {
            this.map.setZoom(this.getZoom() - 1, this.animateMapZoom);
        },
        opensidebar() {
            this.$emit('opensidebar');
        },
        currentLocationToRoutePlan(property) {
            if (navigator.geolocation) {
                const success = (position) => {
                    const data = {
                        formatted_address: 'Current location',
                    };
                    const coords = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    this.pickFromMap(property, data, coords);
                };
                const error = () => {
                    this.error = {
                        show: true,
                        msg:
                            'Current location is not available, please check your browser settings',
                    };
                    setTimeout(() => {
                        this.error = {
                            show: false,
                            msg: '',
                        };
                    }, 3000);
                };
                navigator.geolocation.getCurrentPosition(success, error, {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0,
                });
            }
        },
        findSearch(query, property) {
            let search = null;
            if (query.name) {
                search = query.name;
            }

            axios
                .get('/api/v1/geocoding/query', {
                    params: {
                        query: search,
                    },
                })
                .then(({ data }) => {
                    if (data[0]) {
                        const coordinates = {
                            lat: data[0].coordinates.Latitude,
                            lng: data[0].coordinates.Longitude,
                        };

                        this.centerToPosition(coordinates);

                        if (property) {
                            this.route[property].coordinates = coordinates;
                            this.removeMarkersByType(property);
                            this.drawMarker(coordinates, property, property);
                            this.calculateRoute();
                        }
                    } else {
                        this.showError('Something went wrong. Try again');
                    }
                })
                .catch(() => {
                    this.showError('Something went wrong. Try again');
                });
        },
        findReverse(coord) {
            return axios
                .get('/api/v1/geocoding/reverse', {
                    params: {
                        lat: coord.lat,
                        lng: coord.lng,
                    },
                })
                .catch(() => {
                    throw new Error('Not found');
                    // this.showError('Something went wrong. Try again');
                });
        },
        removeMarkersByType(type, index = null) {
            Object.keys(this.markers).forEach((markerKey) => {
                const marker = this.markers[markerKey];
                const data = marker.getData();
                let compareLeft = data.type;
                let compareRight = type;
                if (type === 'waypoint' && index !== null) {
                    compareLeft = data.index;
                    compareRight = index;
                }
                if (compareLeft === compareRight) {
                    this.map.removeObjects([marker]);
                    delete this.markers[markerKey];
                }
            });
        },
        drawMarker(coordinates, style, type, index = null) {
            // Figure out icon
            let icon = null;
            if (style === 'origin') {
                icon = this.getMarkerIconRouteOrigin('#004ECB');
            } else if (style === 'destination') {
                icon = this.getMarkerIconRouteFinish('#000000');
            } else if (style === 'waypoint') {
                icon = this.getMarkerIconRouteWaypoint('#C8532B');
            } else {
                icon = this.getMarkerRasterIcon(style);
            }

            const marker = new H.map.Marker(
                {
                    lat: coordinates.lat,
                    lng: coordinates.lng,
                },
                {
                    icon,
                    data: {
                        style,
                        type,
                        index,
                    },
                },
            );

            this.map.addObject(marker);
            this.markers.push(marker);
        },
        getMarkerIconRouteOrigin(color) {
            const svgMarkup = `<svg width="28" height="28" xmlns="http://www.w3.org/2000/svg" fill="currentColor"><g fill="none" fill-rule="evenodd"><circle fill="#2C47A2" cx="14" cy="14" r="14"/><circle stroke="${color}" cx="14" cy="14" r="13.5"/><circle fill="${color}" cx="14" cy="14" r="7"/><circle fill="#FFF" cx="14" cy="14" r="6"/></g></svg>`;

            const dotIcon = new H.map.Icon(svgMarkup, {
                anchor: { x: 14, y: 14 },
            });
            return dotIcon;
        },
        getMarkerIconRouteFinish(color) {
            const svgMarkup = `<svg width="36" height="46" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><defs><path d="M30.728 5.39c6.932 7.085 7.028 18.512.289 25.719l-.29.302L18 46 5.272 31.41c-7.03-7.185-7.03-18.835 0-26.02a17.725 17.725 0 0125.456 0z" id="a"/></defs><g fill="none" fill-rule="evenodd"><g><mask id="b" fill="#fff"><use xlink:href="#a"/></mask><use fill="#FFF" xlink:href="#a"/><g mask="url(#b)" fill="${color}"><path d="M1.125-1.15h11.25v11.5H1.125zM1.125 21.85h11.25v11.5H1.125zM12.375 10.35h11.25v11.5h-11.25zM12.375 33.35h11.25v11.5h-11.25zM23.625-1.15h11.25v11.5h-11.25zM23.625 21.85h11.25v11.5h-11.25zM34.875 10.35h11.25v11.5h-11.25zM34.875 33.35h11.25v11.5h-11.25z"/></g></g><path d="M30.37 5.739C26.955 2.247 22.478.5 18 .5 13.523.5 9.046 2.247 5.63 5.739 2.21 9.235.5 13.818.5 18.4c0 4.587 1.713 9.174 5.14 12.671L18 45.24l12.646-14.467c3.287-3.511 4.904-8.054 4.853-12.58-.05-4.513-1.76-9.01-5.128-12.454z" stroke="#443832"/></g></svg>`;

            const dotIcon = new H.map.Icon(svgMarkup, {
                anchor: { x: 18, y: 46 },
            });
            return dotIcon;
        },
        getMarkerIconRouteWaypoint(color) {
            const svgMarkup = `<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" fill="currentColor"><g fill="none" fill-rule="evenodd"><circle fill="#C8532B" cx="10" cy="10" r="10"/><circle stroke="${color}" cx="10" cy="10" r="9.5"/><circle fill="${color}" cx="10" cy="10" r="5"/><circle fill="#FFF" cx="10" cy="10" r="4"/></g></svg>`;

            const dotIcon = new H.map.Icon(svgMarkup, {
                anchor: { x: 10, y: 10 },
            });
            return dotIcon;
        },
        getMarkerIcon(color) {
            if (Object.keys(this.rasterIcons).indexOf(`marker-${color}`) > -1) {
                return this.getMarkerRasterIcon(color);
            }
            return this.getMarkerColorIcon(color);
        },
        getMarkerRasterIcon(icon) {
            if (
                // eslint-disable-next-line operator-linebreak
                !this.markerIcons[`marker-${icon}`] &&
                this.rasterIcons[`marker-${icon}`]
            ) {
                const marker = new H.map.Icon(
                    this.rasterIcons[`marker-${icon}`],
                    {
                        size: { w: 27, h: 32 },
                    },
                );
                this.markerIcons[`marker-${icon}`] = marker;
            } else if (
                // eslint-disable-next-line operator-linebreak
                !this.markerIcons[`marker-${icon}`] &&
                !this.rasterIcons[`marker-${icon}`]
            ) {
                this.markerIcons[`marker-${icon}`] = this.getMarkerColorIcon(
                    '#C8532B',
                );
            }

            return this.markerIcons[`marker-${icon}`];
        },
        getMarkerColorIcon(color) {
            let localColor = color;

            if (typeof localColor === 'undefined') {
                localColor = '#666666';
            }

            const text = '';
            const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Capa_1" x="0px" y="0px" width="32" height="32" viewBox="0 0 263.335 263.335" style="enable-background:new 0 0 263.335 263.335;" xml:space="preserve">
                    <g>
                        <g xmlns="http://www.w3.org/2000/svg">
                            <path d="M40.479,159.021c21.032,39.992,49.879,74.22,85.732,101.756c0.656,0.747,1.473,1.382,2.394,1.839   c0.838-0.396,1.57-0.962,2.178-1.647c80.218-61.433,95.861-125.824,96.44-128.34c2.366-9.017,3.57-18.055,3.57-26.864    C237.389,47.429,189.957,0,131.665,0C73.369,0,25.946,47.424,25.946,105.723c0,8.636,1.148,17.469,3.412,26.28" fill="${localColor}"/>
                        <text x="80" y="130" font-family="sans-serif" font-size="5em" fill="white">${text}</text>
                        </g>
                    </g>
                </svg>`;

            const dotIcon = new H.map.Icon(svgMarkup, {
                anchor: { x: 16, y: 32 },
            });

            return dotIcon;
        },
        removeRoutes() {
            if (this.groups.routes) {
                this.groups.routes.removeAll();
            }
        },
        drawRoute({ data }) {
            const origin = {
                lat: data.route.origin_lat,
                lng: data.route.origin_lng,
            };
            const destination = {
                lat: data.route.destination_lat,
                lng: data.route.destination_lng,
            };

            if (data.route.origin_name) {
                this.route.origin.name = data.route.origin_name;
            }
            if (data.route.destination_name) {
                // eslint-disable-next-line operator-linebreak
                this.route.destination.name = data.route.destination_name;
            }
            if (data.route.origin_lat && data.route.origin_lng) {
                this.route.origin.coordinates = {
                    lat: data.route.origin_lat,
                    lng: data.route.origin_lng,
                };
            }
            if (
                // eslint-disable-next-line operator-linebreak
                data.route.destination_lat &&
                data.route.destination_lng
            ) {
                this.route.destination.coordinates = {
                    lat: data.route.destination_lat,
                    lng: data.route.destination_lng,
                };
            }

            // Duration
            if (data.route.result.duration) {
                this.route.duration = data.route.result.duration;
            }

            // Length
            if (data.route.result.milesLength) {
                this.route.milesLength = data.route.result.milesLength;
            }

            // Arrival
            if (data.route.result.arrival) {
                this.route.arrival = data.route.result.arrival;
            }

            // Share link
            if (data.route.result.shareLink) {
                this.route.shareLink = data.route.result.shareLink;
            } else {
                this.route.shareLink = '';
            }

            // Itinerary
            if (data.route.result.itinerary) {
                this.route.itinerary = data.route.result.itinerary;
            }

            // Draw route
            this.groups.routes = new H.map.Group({});

            if (data.route.result.points.length) {
                this.addPolylineToMap(data.route.result.points, '#8DA8FF', 12);
                this.addPolylineToMap(data.route.result.points, '#004ECB', 8);
            }

            this.map.addObject(this.groups.routes);

            // Zoom to drawn route
            try {
                this.map.getViewModel().setLookAtData(
                    {
                        bounds: this.groups.routes.getBoundingBox(),
                    },
                    true,
                );
            } catch (err) {
                console.log(err);
            }

            // Draw origin/destination markers
            this.removeMarkersByType('origin');
            this.drawMarker(origin, 'origin', 'origin');

            this.removeMarkersByType('destination');
            this.drawMarker(destination, 'destination', 'destination');

            // Draw waypoint markers
            const { waypoints } = data.route;
            if (waypoints.length) {
                waypoints.forEach((waypoint, index) => {
                    const parsed = JSON.parse(waypoint);
                    const found = this.route.waypoints.find(
                        (item) => item.name === parsed.name,
                    );
                    if (!found) {
                        this.route.waypoints.push(parsed);
                        this.drawMarker(
                            parsed.coordinates,
                            'waypoint',
                            parsed.name,
                            index,
                        );
                    }
                });
            }

            // Change route to bookmarkable resource
            if (this.$route.query.guid !== data.route.guid) {
                this.$router.replace({ query: { guid: data.route.guid } });
                // this.$router.push({
                //     name: 'route',
                //     params: { guid: data.route.guid },
                // });
            }

            this.$gtag.event('PLAN_ROUTE_CALCULATED', {
                event_category: 'PLAN',
                value: this.$route.query.guid,
            });

            if (this.isMobile) {
                this.mobileSearch = null;
            }
        },
        loadOverview() {
            const { guid } = this.$route.query;
            this.loading.overview = true;
            axios
                .get('/api/v1/overview', {
                    params: {
                        guid,
                    },
                })
                .then(({ data }) => {
                    this.overview.pois = data.pois;
                })
                .finally(() => {
                    this.loading.overview = false;
                });
        },
        getRoute() {
            this.resetError();
            const origin = this.route.origin.coordinates;
            const destination = this.route.destination.coordinates;

            if (this.$route.query.guid) {
                // this.$router.push({
                //     name: 'home',
                // });
                // return;
            }

            this.axiosCancelToken = axios.CancelToken.source();

            this.calculating = true;

            axios
                .get('/api/v1/routes/find', {
                    cancelToken: this.axiosCancelToken.token,
                    params: {
                        origin_lat: origin.lat,
                        origin_lng: origin.lng,
                        destination_lat: destination.lat,
                        destination_lng: destination.lng,
                        origin_name: this.route.origin.name,
                        destination_name: this.route.destination.name,
                        settings: this.settings,
                        truck: this.truckPreset,
                        via: this.route.waypoints,
                    },
                })
                .then((response) => {
                    this.drawRoute(response);
                    this.calculating = false;
                })
                .then((response) => {
                    if (this.showPois) {
                        this.loadOverview(response);
                    }
                })
                .catch((e) => {
                    if (e.response || e.request) {
                        if (e.response.status === 404) {
                            this.showError(
                                'Check your truck presets, if waypoints are on the route or if you have additional restricts such as avoid tolls or unpaved roads.',
                            );
                        } else {
                            this.showError('Something went wrong. Try again');
                        }
                        this.$gtag.event('PLAN_ROUTE_FAILED', {
                            event_category: 'PLAN',
                        });
                    } else if (!axios.isCancel(e)) {
                        this.showError(
                            'Check your truck presets, if waypoints are on the route or if you have additional restricts such as avoid tolls or unpaved roads.',
                        );
                        this.$gtag.event('PLAN_ROUTE_FAILED', {
                            event_category: 'PLAN',
                        });
                    }
                    this.calculating = false;
                });
        },
        loadRoute(guid) {
            axios
                .get('/api/v1/routes/load', {
                    params: {
                        guid,
                    },
                })
                .then((response) => {
                    // if (this.isMobile) {
                    //     this.redirectToApp(response);
                    // }
                    this.drawRoute(response);
                })
                .then((response) => {
                    if (this.showPois) {
                        this.loadOverview(response);
                    }
                })
                .catch(() => {
                    this.showError('Something went wrong. Try again');
                    this.$gtag.event('PLAN_ROUTE_FAILED', {
                        event_category: 'PLAN',
                        value: guid,
                    });
                });
        },
        updateRoute(route) {
            this.route = route;
        },
        calculateRoute() {
            if (
                // eslint-disable-next-line
                this.route.origin.coordinates &&
                this.route.destination.coordinates
            ) {
                if (this.axiosCancelToken) {
                    this.axiosCancelToken.cancel();
                }
                // Remove existing routes
                this.removeRoutes();
                this.getRoute();
            }
        },
        addPolylineToMap(points, color, width) {
            const lineString = new H.geo.LineString();

            // eslint-disable-next-line
            points.map((point) => {
                lineString.pushPoint({ lat: point[0], lng: point[1] });
            });

            const polyline = new H.map.Polyline(
                lineString,
                this.getPolylineStyle({
                    strokeColor: color,
                    lineWidth: width,
                }),
            );

            this.groups.routes.addObject(polyline);
        },
        getPolylineStyle({ strokeColor, lineWidth }) {
            let localStrokeColor = strokeColor;
            if (typeof strokeColor === 'undefined') {
                localStrokeColor = 'rgba(0, 128, 255, 0.7)';
            }

            let localLineWidth = lineWidth;
            if (typeof lineWidth === 'undefined') {
                localLineWidth = 8;
            }
            return {
                style: {
                    lineWidth: localLineWidth,
                    strokeColor: localStrokeColor,
                },
            };
        },
        createContextMenu() {
            this.map.addEventListener('contextmenu', (e) => {
                if (e.target !== this.map) {
                    return;
                }

                this.$gtag.event('MAP_SELECT_OPEN', {
                    event_category: 'MAP',
                });

                const coord = this.map.screenToGeo(e.viewportX, e.viewportY);

                this.$gtag.event('MAP_SELECT_OPEN', {
                    event_category: 'MAP',
                });

                const localityHeader = new H.util.ContextItem({
                    label: ' ',
                    disabled: true,
                });
                const originButton = new H.util.ContextItem({
                    label: 'Set Origin',
                    callback: () => {
                        this.$gtag.event('MAP_SELECT_ORIGIN', {
                            event_category: 'MAP',
                        });

                        this.findReverse(coord).then(({ data }) => {
                            if (data[0]) {
                                this.pickFromMap('origin', data[0], coord);
                            }
                        });
                    },
                });
                const destinationButton = new H.util.ContextItem({
                    label: 'Set Destination',
                    callback: () => {
                        this.$gtag.event('MAP_SELECT_DESTINATION', {
                            event_category: 'MAP',
                        });

                        this.findReverse(coord).then(({ data }) => {
                            if (data[0]) {
                                this.pickFromMap('destination', data[0], coord);
                            }
                        });
                    },
                });
                const waypointButton = new H.util.ContextItem({
                    label: 'Set Waypoint',
                    callback: () => {
                        this.$gtag.event('MAP_SELECT_WAYPOINT', {
                            event_category: 'MAP',
                        });

                        this.findReverse(coord).then(({ data }) => {
                            if (data[0]) {
                                this.pickFromMap('waypoint', data[0], coord);
                            }
                        });
                    },
                });

                e.items.push(
                    localityHeader,
                    originButton,
                    destinationButton,
                    waypointButton,
                );

                this.findReverse(coord)
                    .then(({ data }) => {
                        localityHeader.setLabel(
                            `${data[0].subLocality}, ${data[0].state}`,
                        );
                    })
                    .catch(() => {
                        document.querySelector('div.H_context_menu').remove();
                    });
            });
        },
        pickFromMap(property, data, coord, center, calculate = true) {
            this.removeRoutes();
            const coordinates = {
                lat: coord.lat,
                lng: coord.lng,
            };

            if (center) {
                this.centerToPosition(coordinates);
            }

            if (property) {
                let type = property;
                let index = null;
                if (property === 'waypoint') {
                    const waypoint = {
                        coordinates: {
                            lat: coord.lat,
                            lng: coord.lng,
                        },
                    };
                    waypoint.name = this.preparePointName(data);
                    this.route.waypoints.push(waypoint);
                    index = this.route.waypoints.length - 1;
                    type = waypoint.name;
                } else {
                    this.route.block[property] = false;
                    this.route[property].coordinates = coordinates;
                    this.route[property].name = this.preparePointName(data);
                    this.removeMarkersByType(property);
                }

                this.drawMarker(coordinates, property, type, index);
                if (calculate) {
                    this.calculateRoute();
                }
            }
        },

        showError(msg) {
            this.error = {
                show: true,
                msg,
            };
        },
        resetError() {
            this.error = {
                show: false,
                msg: '',
            };
        },
        loadPois() {
            axios
                .get('/api/v1/poi/all')
                .then(({ data }) => {
                    this.pois = data.pois;
                })
                .catch((error) => {
                    console.log(error);
                });
        },
        drawPois() {
            const group = new H.map.Group({});
            const icon = this.getMarkerIcon('#222222');

            this.pois.forEach((poi) => {
                const marker = new H.map.Marker(
                    {
                        lat: poi.lat,
                        lng: poi.lng,
                    },
                    {
                        icon,
                        data: {
                            id: poi.id,
                        },
                    },
                );

                group.addObject(marker);
            });

            this.map.addObject(group);
        },
        drawClusters() {
            if (this.clusterLayer) {
                this.map.removeLayer(this.clusterLayer);
            }

            const dataPoints = this.filteredPois.map(
                (item) =>
                    // H.clustering.DataPoint(latitude, longitude, altitude, reference to data)
                    new H.clustering.DataPoint(item.lat, item.lng, null, {
                        guid: item.guid,
                        icon: item.icon,
                    }),
            );

            const clusteredDataProvider = new H.clustering.Provider(
                dataPoints,
                {
                    clusteringOptions: {
                        // Supported strategies: FASTGRID|GRID|DYNAMICGRID
                        // strategy: H.clustering.Provider.Strategy.DYNAMICGRID,
                        strategy: H.clustering.Provider.Strategy.DYNAMICGRID,
                        // Maximum radius of the neighbourhood
                        eps: 128,
                        // Minimum weight of points required to form a cluster
                        minWeight: 2,
                        // Spherical Mercator projection
                        // projection: H.geo.mercator,
                    },
                },
            );

            clusteredDataProvider.addEventListener('tap', this.onMarkerClick);

            // Get default cluster theme
            // const defaultTheme = clusteredDataProvider.getTheme();

            // Prepare marker icons
            const preparedIcons = [];

            Object.keys(rasterIcons).forEach((item) => {
                const icon = item.replace('marker-', '');
                preparedIcons[icon] = new H.map.Icon(
                    this.rasterIcons[`marker-${icon}`],
                    {
                        size: { w: 27, h: 32 },
                    },
                );
            });

            // Modify default cluster theme
            // eslint-disable-next-line no-unused-vars
            const customTheme = {
                getClusterPresentation(cluster) {
                    const weight = cluster.getWeight();

                    const styles = {
                        'weight < 10': {
                            size: 28,
                            stroke: 4,
                            color: '172, 159, 150',
                            textPosition: { x: 14, y: 20 },
                        },
                        'weight < 25': {
                            size: 38,
                            stroke: 5,
                            color: '200, 83, 43',
                            textPosition: { x: 19, y: 25 },
                        },
                        'weight < 50': {
                            size: 38,
                            stroke: 6,
                            color: '123, 9, 3',
                            textPosition: { x: 19, y: 25 },
                        },
                        'weight < 100': {
                            size: 38,
                            stroke: 8,
                            color: '134, 45, 14',
                            textPosition: { x: 19, y: 25 },
                        },
                        'weight < 1000': {
                            size: 48,
                            stroke: 12,
                            color: '200, 83, 43',
                            textPosition: { x: 24, y: 30 },
                        },
                        'weight > 1000': {
                            size: 66,
                            stroke: 20,
                            color: '200, 83, 43',
                            textPosition: { x: 33, y: 38 },
                        },
                    };

                    let style = styles['weight < 10'];

                    if (weight < 10) {
                        style = styles['weight < 10'];
                    } else if (weight < 25) {
                        style = styles['weight < 25'];
                    } else if (weight < 50) {
                        style = styles['weight < 50'];
                    } else if (weight < 100) {
                        style = styles['weight < 100'];
                    } else if (weight < 1000) {
                        style = styles['weight < 1000'];
                    } else if (weight > 1000) {
                        style = styles['weight > 1000'];
                    }

                    style.diameter = style.size / 2 - style.stroke / 2;
                    style.center = style.size / 2;
                    style.line = style.size - style.stroke;
                    style.padding = style.stroke / 2;

                    // Build DOM icon
                    const clusterIcon = new H.map.DomIcon(`
                        <div style="width: ${style.size}px; height: ${style.size}px; text-align: center; color: #fff; font-family: 'Barlow Semi Condensed'; font-weight: 500;  border-radius: 50%; background-color: rgba(${style.color}, 0.5); user-select: none; padding: ${style.padding}px">
                            <div style="width: ${style.line}px; height: ${style.line}px; background-color: rgb(${style.color}); border-radius: 50%; line-height: ${style.line}px">
                                ${weight}
                            </div>
                        </div>
                    `);

                    // Create a marker for clusters:
                    const clusterMarker = new H.map.DomMarker(
                        cluster.getPosition(),
                        {
                            icon: clusterIcon,
                            // Set min/max zoom with values from the cluster, otherwise
                            // clusters will be shown at all zoom levels:
                            min: cluster.getMinZoom(),
                            max: cluster.getMaxZoom(),
                        },
                    );

                    // SVG Build cluster icon
                    /*
                    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" height="${style.size}px" width="${style.size}px"><circle cx="${style.center}px" cy="${style.center}px" r="${style.diameter}" fill="rgb(${style.color})" stroke="rgb(${style.color})" stroke-width="${style.stroke}" stroke-opacity="0.5" />
                            <text x="${style.textPosition.x}" y="${style.textPosition.y}"
                            font-size="11pt" font-family="Barlow Semi Condensed"
                            font-weight="400" text-anchor="middle" fill="white">${weight}</text>
                        </svg>`;

                    const clusterIcon = new H.map.Icon(svgString, {
                        size: { w: style.size, h: style.size },
                        anchor: { x: style.size / 2, y: style.size / 2 },
                    });

                    // Create a marker for clusters:
                    const clusterMarker = new H.map.Marker(
                        cluster.getPosition(),
                        {
                            icon: clusterIcon,
                            // Set min/max zoom with values from the cluster, otherwise
                            // clusters will be shown at all zoom levels:
                            min: cluster.getMinZoom(),
                            max: cluster.getMaxZoom(),
                        },
                    );
                    */

                    return clusterMarker;
                },
                getNoisePresentation(noisePoint) {
                    // Get a reference to data object from noise points
                    const data = noisePoint.getData();

                    // Create a marker for the noisePoint
                    const iconMarker = new H.map.Marker(
                        noisePoint.getPosition(),
                        {
                            icon: preparedIcons[data.icon],
                            data: {
                                id: data.guid,
                            },
                        },
                    );

                    // Link a data from the point to the marker
                    // to make it accessible inside onMarkerClick
                    iconMarker.setData(data);

                    return iconMarker;
                },
            };

            // Set modified cluster theme
            clusteredDataProvider.setTheme(customTheme);

            // Create clustering layer
            const clusteringLayer = new H.map.layer.ObjectLayer(
                clusteredDataProvider,
            );

            this.clusterLayer = clusteringLayer;

            this.map.addLayer(clusteringLayer);
        },
        onMarkerClick(event) {
            // Get the data associated with that marker
            const data = event.target.getData();
            // Merge default template with the data and get HTML
            this.getBubbleContent(data).then(this.drawBubble);
        },
        onPoiDetailRouted(guid) {
            const vm = this;
            this.map.getEngine().addEventListener('render', function cb(event) {
                if (vm.map.getEngine() === event.target) {
                    vm.getBubbleContent({ guid }).then((response) => {
                        vm.map.getViewModel().setLookAtData(
                            {
                                position: {
                                    lat: response.data.poi.lat,
                                    lng: response.data.poi.lng,
                                },
                                zoom: 14,
                            },
                            true,
                        );
                        vm.drawBubble(response, false);
                        event.currentTarget.removeEventListener(event.type, cb);
                    });
                }
            });
        },
        drawBubble({ data }, setCenter = true) {
            const position = {
                lat: data.poi.lat,
                lng: data.poi.lng,
            };

            let { bubble } = this.onMarkerClick;

            const bubbleContent = this.getBubbleFormat(data.poi);

            // For all markers create only one bubble, if not created yet
            if (!bubble) {
                bubble = new H.ui.InfoBubble(position, {
                    content: bubbleContent,
                });
                this.ui.addBubble(bubble);
                // Cache the bubble object
                this.onMarkerClick.bubble = bubble;
            } else {
                // Reuse existing bubble object
                bubble.setPosition(position);
                bubble.setContent(bubbleContent);
                bubble.open();
            }

            // Center to position of the marker
            // do this only if marker is not in viewport
            if (setCenter) {
                this.map.setCenter(position, true);
            }

            if (this.$route.params.poiguid !== data.poi.guid) {
                this.$router.push({
                    name: 'detail',
                    params: { poiguid: data.poi.guid },
                });
            }
        },
        getBubbleContent(item) {
            return axios.get(`/api/v1/poi/find/${item.guid}`).catch(() => {
                this.showError('POI not found');
            });
        },
        getBubbleFormat({
            title,
            amenities_named: amenitiesNamed,
            icon,
            count_diesel: countDiesel,
            count_def: countDef,
            count_parking: countParking,
            count_showers: countShowers,
            lat,
            lng,
        }) {
            let amenitiesIconsString = '';

            const countableDisplayed = [];

            if (countDiesel) {
                amenitiesIconsString += `
                    <svg
                        style="width: 24px; height: 24px; fill: rgba(40, 27, 20, 0.87)"
                        viewBox="0 0 24 24"
                        class="poi-icon"
                        role="img"
                    >
                        <title>Diesel</title>
                        <path d="${this.amenitiesIcons.gas}" />
                    </svg>
                    <span class="amenity__count">${countDiesel}</span>
                `;

                countableDisplayed.push('diesel');
            }

            if (countParking) {
                amenitiesIconsString += `
                    <svg
                        style="width: 24px; height: 24px; fill: rgba(40, 27, 20, 0.87)"
                        viewBox="0 0 24 24"
                        class="poi-icon"
                        role="img"
                    >
                        <title>Parking</title>
                        <path d="${this.amenitiesIcons.parking}" />
                    </svg>
                    <span class="amenity__count">${countParking}</span>
                `;

                countableDisplayed.push('parking');
            }

            if (countDef) {
                amenitiesIconsString += `
                    <svg
                        style="width: 24px; height: 24px; fill: rgba(40, 27, 20, 0.87)"
                        viewBox="0 0 24 24"
                        class="poi-icon"
                        role="img"
                    >
                        <title>DEF</title>
                        <path d="${this.amenitiesIcons.def}" />
                    </svg>
                    <span class="amenity__count">${countDef}</span>
                `;

                countableDisplayed.push('def');
            }

            if (countShowers) {
                amenitiesIconsString += `
                    <svg
                        style="width: 24px; height: 24px; fill: rgba(40, 27, 20, 0.87)"
                        viewBox="0 0 24 24"
                        class="poi-icon"
                        role="img"
                    >
                        <title>Showers</title>
                        <path d="${this.amenitiesIcons.showers}" />
                    </svg>
                    <span class="amenity__count">${countShowers}</span>
                `;

                countableDisplayed.push('showers');
            }

            if (amenitiesNamed) {
                amenitiesNamed.forEach((amenitySlug) => {
                    if (
                        // eslint-disable-next-line operator-linebreak
                        this.amenitiesIcons[amenitySlug] &&
                        countableDisplayed.indexOf(amenitySlug) === -1
                    ) {
                        amenitiesIconsString += `
                            <svg
                                style="width: 24px; height: 24px; fill: rgba(40, 27, 20, 0.87)"
                                viewBox="0 0 24 24"
                                class="poi-icon"
                                role="img"
                            >
                                <title>${amenitySlug}</title>
                                <path d="${this.amenitiesIcons[amenitySlug]}" />
                            </svg>
                        `;
                    } else {
                        console.warn(`Missing icon for ${amenitySlug}`);
                    }
                });
            }

            const iconDirections = `
                <svg style="width: 24px; height: 24px; color: #fff;">
                    <use xlink:href="${this.icons.directions}#icon"></use>
                </svg>
            `;

            const iconName = `marker-${icon}`;

            let iconLink = '';
            if (this.rasterIcons[iconName]) {
                iconLink = this.rasterIcons[iconName];
            }

            let bubbleString = `
                <div class="bubble">
                    <div class="d-flex">
                        <div class="bubble__preview">
                            <img src="${iconLink}">
                        </div>
                        <div>
                            <h3 class="bubble__title">
                                ${title}
                            </h3>
                            <h4 class="bubble__subtitle">
                                N/A
                            </h4>
                        </div>
                    </div>
                    <div class="bubble__directions">
                        <button type="button" class="btn btn-primary btn-block directions-button" poi-lat="${lat}" poi-lng="${lng}" poi-title="${title}">
                            ${iconDirections}
                            Directions
                        </button>
                    </div>
            `;

            if (amenitiesIconsString) {
                bubbleString += `
                        <div class="bubble__amenities">
                            <h5 class="bubble__section-title">
                                Amenities
                            </h5>
                            <div class="bubble__amenities__icons">
                                ${amenitiesIconsString}
                            </div>
                        </div>
                `;
            }

            bubbleString += `
                </div>
            `;

            return bubbleString;
        },
        handleDirectionsButton() {
            const vm = this;
            document
                .querySelector('.map__wrapper')
                .addEventListener('click', (e) => {
                    if (e.target.classList.contains('directions-button')) {
                        // Origin
                        const chooseOrigin = () => {
                            vm.error = {
                                show: true,
                                msg: 'Please choose starting point',
                            };
                            vm.$refs.originInput.$refs.input.focus();
                        };

                        if (!vm.route.origin.coordinates) {
                            if (navigator.geolocation) {
                                const success = (position) => {
                                    vm.route.origin.name = 'Current position';
                                    vm.route.origin.coordinates = {
                                        lat: position.coords.latitude,
                                        lng: position.coords.longitude,
                                    };
                                };
                                navigator.geolocation.getCurrentPosition(
                                    success,
                                    chooseOrigin,
                                    {
                                        enableHighAccuracy: true,
                                        timeout: 5000,
                                        maximumAge: 0,
                                    },
                                );
                            } else {
                                chooseOrigin();
                            }
                        }

                        // Destination
                        const lat = e.target.getAttribute('poi-lat');
                        const lng = e.target.getAttribute('poi-lng');
                        const title = e.target.getAttribute('poi-title');
                        vm.route.destination.name = title;
                        vm.route.destination.coordinates = {
                            lat,
                            lng,
                        };

                        vm.calculateRoute();
                    }
                });
        },
        passPrimaryFilter(filter) {
            this.$emit('selectprimaryfilter', filter);
        },
        passSecondaryFilter(filter) {
            this.$emit('selectsecondaryfilter', filter);
        },
        setMobileSearch(value) {
            this.mobileSearch = value;
        },
        drawOverviewMarkers() {
            if (this.groups.overview) {
                this.groups.overview.removeAll();
            }

            this.groups.overview = new H.map.Group({});

            Object.values(this.overview.pois).forEach((poi) => {
                const marker = new H.map.Marker(
                    {
                        lat: poi.lat,
                        lng: poi.lng,
                    },
                    {
                        icon: this.getMarkerRasterIcon(poi.icon),
                        data: {
                            guid: poi.guid,
                        },
                    },
                );

                this.groups.overview.addObject(marker);
            });

            this.groups.overview.addEventListener('tap', this.onMarkerClick);

            this.map.addObject(this.groups.overview);
        },
        solveDeepLink(deepLink) {
            const { type } = deepLink;
            if (type === 'origin') {
                this.deepLinkOrigin(deepLink.data);
            } else if (type === 'route') {
                this.deepLinkRoute(deepLink);
            }
        },
        deepLinkOrigin(data) {
            if (data.type === 404) {
                this.showError(data.msg);
            } else {
                const coordinates = {
                    lat: data.coordinates.Latitude,
                    lng: data.coordinates.Longitude,
                };
                this.centerToPosition(coordinates);
                this.pickFromMap('origin', data, coordinates);
            }
        },
        deepLinkRoute(data) {
            // Origin
            if (data.origin.type === 404) {
                this.showError(data.origin.msg);
            } else if (data.origin === 'current-location') {
                this.currentLocationToRoutePlan('origin');
            } else {
                const originCoords = {
                    lat: data.origin.coordinates.Latitude,
                    lng: data.origin.coordinates.Longitude,
                };
                this.pickFromMap('origin', data.origin, originCoords);
            }

            if (data.waypoints.length) {
                data.waypoints.forEach((waypoint) => {
                    if (waypoint.type === 404) {
                        this.showError(waypoint.msg);
                    } else {
                        const waypointCoords = {
                            lat: waypoint.coordinates.Latitude,
                            lng: waypoint.coordinates.Longitude,
                        };
                        this.pickFromMap('waypoint', waypoint, waypointCoords);
                    }
                });
            }

            if (data.destination.type === 404) {
                this.showError(data.destination.msg);
            } else if (data.destination === 'current-location') {
                this.currentLocationToRoutePlan('destination');
            } else {
                const destinationCoords = {
                    lat: data.destination.coordinates.Latitude,
                    lng: data.destination.coordinates.Longitude,
                };
                this.pickFromMap(
                    'destination',
                    data.destination,
                    destinationCoords,
                );
            }
        },
        preparePointName(data) {
            if (data.formatted_address) {
                return data.formatted_address;
            }

            const firstPart = data.subLocality
                ? data.subLocality
                : data.locality;
            const secondPart = data.state ? data.state : '';

            return `${firstPart} ${secondPart}`;
        },
        removeWaypointMarker(index) {
            this.removeMarkersByType('waypoint', index);
        },
        updateWaypoint(index) {
            const waypoint = this.route.waypoints[index];
            const search = waypoint.name;
            this.removeWaypointMarker(index);
            axios
                .get('/api/v1/geocoding/query', {
                    params: {
                        query: search,
                    },
                })
                .then(({ data }) => {
                    if (data[0]) {
                        const coordinates = {
                            lat: data[0].coordinates.Latitude,
                            lng: data[0].coordinates.Longitude,
                        };

                        this.route.waypoints[index].coordinates = coordinates;

                        this.drawMarker(coordinates, 'waypoint', search, index);

                        const origin = this.route.origin.name;
                        const destination = this.route.origin.name;

                        if (origin && destination) {
                            this.calculateRoute();
                        }
                    } else {
                        this.showError('Something went wrong. Try again');
                    }
                })
                .catch(() => {
                    this.showError('Something went wrong. Try again');
                });
        },
        redirectToApp({ data }) {
            const { route } = data;
            let waypoints = '';
            if (route.waypoints.length) {
                route.waypoints.forEach((single) => {
                    const waypoint = JSON.parse(single);
                    waypoints += `${waypoint.coordinates.lat},${waypoint.coordinates.lng}/`;
                });
            }
            window.location = `hammer://app/route/${route.origin_lat},${route.origin_lng}/${waypoints}${route.destination_lat},${route.destination_lng}`;
        },
        updateTruckPresets(presets) {
            window.config.truck_presets = presets;
            this.options.truck_preset = [
                Object.values(window.config.truck_presets).map((item) => {
                    // eslint-disable-next-line no-param-reassign
                    item.value = item.type;
                    return item;
                }),
            ];
            this.calculateRoute();
        },
        addNewDestination() {
            if (this.route.destination.name) {
                this.pickFromMap(
                    'waypoint',
                    {
                        formatted_address: this.route.destination.name,
                    },
                    {
                        lat: this.route.destination.coordinates.lat,
                        lng: this.route.destination.coordinates.lng,
                    },
                    false,
                    false,
                );
                this.route.destination = {
                    corodinates: {
                        lat: '',
                        lng: '',
                    },
                    full: '',
                    id: '',
                    name: '',
                };
                this.removeMarkersByType('destination');
            } else {
                this.route.waypoints.push({
                    coordinates: {
                        lat: '',
                        lng: '',
                    },
                    name: '',
                });
            }
        },
    },
};
</script>

<style lang="scss">
/* ==========================================================================
   Bubble
   ========================================================================== */
.H_ib_body {
    box-shadow: 0 6px 15px 0 rgba(0, 0, 0, 0.1);
    bottom: 30px;
    .H_ib_content {
        margin: 0;
    }
    .H_ib_close.H_btn {
        right: 15px;
        top: 15px;
    }
}

.H_ib_tail {
    bottom: 35px;
}

.bubble {
    width: 424px;
    padding: 20px;
    font-family: 'Barlow Semi Condensed';

    .bubble__preview {
        max-width: 40px;
        min-width: 40px;
        height: 40px;
        margin-right: 10px;
        text-align: center;
        line-height: 40px;
    }

    .bubble__title {
        font-size: 24px;
        line-height: 28px;
        color: #4e3a30;
        font-weight: 600;
        margin-bottom: 0;
    }

    .bubble__subtitle {
        font-size: 16px;
        line-height: 24px;
        color: #4e3a30;
        font-weight: 600;
    }

    .btn.btn-primary {
        box-sizing: border-box;
        border: 1px solid #c95227;
        border-color: #c95227;
        background-color: #c95227;
        color: #fff;
        border-radius: 4px;
        height: 36px;
        line-height: 34px;
        text-transform: uppercase;
        white-space: nowrap;
        font-weight: 600;
        font-size: 14px;
        letter-spacing: 1px;
        transition: background-color 0.3s ease;
        padding: 0;

        &:hover {
            background-color: #4e3a30;
            border-color: #4e3a30;
        }
    }

    .bubble__amenities {
        margin-top: 15px;
        background-color: rgba(0, 0, 0, 0.05);
        padding: 20px 15px 10px;

        .bubble__section-title {
            font-size: 16px;
            color: #c8532b;
        }
    }

    .bubble__amenities__icons {
        display: flex;
        align-items: center;
        height: 40px;
        svg {
            flex: 1 0 auto;
            max-width: 32px;
        }

        .amenity__count {
            color: #4e3a30;
            font-weight: 600;
            margin-left: -5px;
            margin-right: 5px;
        }
    }
}

/* ==========================================================================
   Map
   ========================================================================== */
.map__canvas {
    @media screen and (max-width: 768px) {
        &.padded {
            padding-bottom: 100px;
        }
    }

    &,
    canvas {
        background-color: #eeeeee !important;
    }
}

/* ==========================================================================
   Controls UI
   ========================================================================== */
@mixin map-ui-hover() {
    transition: background-color 300ms;

    &:hover {
        background-color: #eee2d9;
    }
}

.H_l_bottom.H_l_right {
    right: calc(100% + 24px);

    .H_ctl.H_el {
        margin: 4px 0;
        border-radius: 4px;
        box-shadow: 0 11px 15px 0 rgba(0, 0, 0, 0.2);
        overflow: hidden;

        &:nth-child(1) {
            @media screen and (min-width: 769px) {
                margin-bottom: 44px;
            }
        }
    }
}

.btn--map-control {
    width: 36px;
    height: 36px;
    padding: 6px;
    border-radius: 0;
    box-sizing: border-box;
    box-shadow: none;
    background-color: #fff;
    @include map-ui-hover;

    & + .btn--map-control {
        position: relative;
        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 28px;
            height: 1px;
            background-color: rgba(78, 58, 48, 0.2);
        }
    }
}
/* ==========================================================================
   Context menu
   ========================================================================== */
.H_context_menu.H_el {
    padding: 8px 10px 10px;
    box-shadow: 0 6px 15px 0 rgba(0, 0, 0, 0.15);

    .H_context_menu_item {
        font-family: 'Barlow Semi Condensed', sans-serif;
        font-size: 16px;
        line-height: 32px;
        margin-bottom: 0;
        padding-left: 10px;
        padding-right: 10px;
        &.disabled {
            color: #4e3a30;
            font-weight: 600;
            line-height: 40px;
            margin-bottom: 5px;
            padding-left: 0;
            &:before {
                content: '';
                display: inline-block;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                border: 1px solid #bcb4b0;
                float: left;
                margin-right: 8px;
                color: #c8532b;
                background-image: url(/images/icons/place-context-menu.svg);
                background-repeat: no-repeat;
                background-position: center center;
                background-size: 26px 26px;
            }
        }
        &:not(.disabled) {
            color: #4e3a30;
            font-weight: 500;
            &:hover {
                color: #c8532b;
            }
        }
    }
}

/* ==========================================================================
   Widget: Route planning
   ========================================================================== */

.widget__route-planning {
    width: 334px;
    border-radius: 4px;
    background-color: #fff;
    position: fixed;
    top: 17px;
    left: 19px;
    box-shadow: 0 6px 15px 0 rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 34px);
    z-index: 12;

    @media screen and (max-width: 1700px) {
        &.bannerOffset {
            max-height: calc(100vh - 102px);
        }
    }

    @media screen and (max-width: 768px) {
        width: 100%;
        top: 0;
        left: 0;
        z-index: 3;

        &.opened {
            height: 100vh;
            display: flex;
            flex-direction: column;
        }
    }

    h3 {
        height: 48px;
        line-height: 48px;
        white-space: nowrap;
        text-align: center;
        margin: 0;
        font-size: 20px;
        color: #4e3a30;
    }
}

.widget__route-planning__content {
    padding-top: 12px;
    padding-left: 16px;
    padding-right: 16px;
}

.widget__route-planning__burger {
    position: absolute;
    left: 14px;
    top: 14px;
    width: 24px;
    height: 24px;
    cursor: pointer;
    color: #281b14;
    transition: color 0.3s ease;

    &:hover {
        color: #c8532b;
    }
}

.widget__route-planning__icon,
.widget__route-planning__icon--control {
    width: 24px;
    height: 24px;

    &.add-icon {
        background-color: #c8532b;
        border-radius: 100%;
        transform: scale(0.8);
        cursor: pointer;
    }
}

.widget__route-planning__icon--control {
    cursor: pointer;
}

.input-text__wrapper + .input-text__wrapper,
.input-search__wrapper + .input-search__wrapper {
    margin-top: 12px;
}

.widget__route-planning__row {
    & > div {
        height: 48px;
        .input-drop__wrapper.wrapper--seamless,
        .input-drop__wrapper.wrapper--seamless-combined {
            &,
            .input-drop__value {
                height: 48px;
                line-height: 48px;
            }

            .caret {
                top: 10px;
            }
        }

        .input-drop__wrapper.wrapper--seamless-combined {
            .input-drop__value--current {
                position: absolute;
                right: 0;
            }

            .caret {
                top: 12px;
                right: -32px;
            }

            .input-drop__dropdown {
                margin-left: 47px;
                margin-top: -8px;
                padding-top: 1px;
                padding-bottom: 1px;
            }
        }
    }
    & > div {
        &:nth-child(2) {
            border-top: 1px solid rgba(78, 58, 48, 0.2);
        }
    }

    /**
     * Truck preset mobile
     */
    @media screen and (max-width: 769px) {
        &.truck-preset {
            .flex-fill {
                height: auto;

                .opened {
                    height: auto;
                    margin-bottom: 20px;

                    .input-drop__value {
                        height: auto;

                        .caret {
                            transform: rotate(90deg);
                        }

                        .input-drop__dropdown {
                            position: static;
                            width: 100%;
                            box-shadow: none;
                            margin-left: 0;
                            margin-right: 0;
                        }
                    }
                }
            }
        }
    }
}

#widget__route-planning__points {
    position: relative;
    &:before {
        content: '';
        position: absolute;
        top: 32px;
        left: 50%;
        width: 1px;
        bottom: 32px;
        transform: translate(-50%, 0);
        border-left: 1px dashed #bcb4b0;
        margin-left: -4px;
        z-index: -1;
    }
}

.color--blue {
    color: #004ecb;
}

.color--orange {
    color: #c8532b;
}

.color--brown {
    color: rgba(40, 27, 20, 0.87);
}

.icon--scaled {
    transform: scale(1.2);
}

.icon--downscaled {
    transform: scale(0.8);
}

.divider {
    border-top: 1px solid rgba(78, 58, 48, 0.2);
    margin-top: 20px;
    margin-bottom: 0px;
}

.suggestions {
    position: absolute;
    z-index: 1;
    background: #fff;
    padding: 0;
    border-radius: 0 0 6px 6px;
    left: 48px;
    right: 48px;
    box-shadow: 0 6px 15px 0 rgb(0 0 0 / 8%);
    overflow: hidden;
    max-height: 275px;
    overflow-y: auto;

    &.suggestions--origin {
        top: 100px;
    }

    &.suggestions--destination {
    }

    .suggestion {
        padding-top: 6px;
        padding-bottom: 6px;
        padding-left: 12px;
        padding-right: 12px;
        cursor: pointer;

        h6 {
            margin-bottom: 0;
            font-size: 12px;
            line-height: 1em;
        }

        small {
            font-size: 11px;
            line-height: 1em;
        }

        & + .suggestion {
            border-top: 1px solid #eee;
        }

        &:hover,
        &.hovered {
            background-color: #f2f2f2;
        }
    }
}
</style>
